import type Card from "@/model/card";
import { SUIT } from "@/model/card";
import { type Config } from "./config";
import type Vulnerability from "@/model/vulnerability";
import { VULNERABILITY } from "@/model/vulnerability";
import { LanguageId } from "./languageids";
import type Board from "@/model/board";
import { PLAYING_MODE } from "@/model/board";
import { SCORING_METHOD } from '@/defines/scoringmethod';
import type Bid from "@/model/bid";
import { BID } from "@/model/bid";
import type Contract from "@/model/contract";
import { CONTRACT, PENALTY } from "@/model/contract";
import type History from "@/model/history";
import type Chair from "@/model/chair";
import { CHAIR } from "@/model/chair";
import { SCORE_TREATMENT } from "@/defines/serverdefines";
import { ContractResult } from "@/model/contractresult";
import type Table from "@/model/table";
import { TABLE_NAME } from "@/model/table";
import Tagger from "@/logic/tagger";
import type { IActivityBase } from "@/model/interfaces/activity";
import ChatTo from "@/model/chatto";
import type Level from "@/model/level";
import type { HasLevels } from "@/model/level";
import { type CONTACT_CODE, CONTACT_CODES } from "@/defines/contactcode";
import { truncateDecimals } from "@/helpers/number";
import { type ITableBase } from "@/model/interfaces/table";
import type Tournament from "@/model/tournament";
import { TournamentStructure, type RegistrationBase } from "@/model/tournament";
import { ActivityType } from "@/defines/activitytype";
import { simpelMDtoHtml } from "@/helpers/html";
import { LanguageFile } from "./languagefile";

const HTML_CLUB_4C = '<span class="suitsymbol suit-clubs-4c">&clubs;</span>'
const HTML_DIAMOND_4C = '<span class="suitsymbol suit-diams-4c">&diams;</span>'
const HTML_CLUB = '<span class="suitsymbol suit-clubs">&clubs;</span>'
const HTML_DIAMOND = '<span class="suitsymbol suit-diams">&diams;</span>'
const HTML_HEART = '<span class="suitsymbol suit-hearts">&hearts;</span>'
const HTML_SPADE = '<span class="suitsymbol suit-spades">&spades;</span>'

export type ContractResultOptions = {
    perspective?: Chair,
    declarer?: Chair,
    tricks?: number,
    lead?: Card,
    scoreNS?: number,
    scoreTreatment?: SCORE_TREATMENT
}

export const CHAR_COPYRIGHT = "\u00E3";
export const CHAR_CLUB = "\u2663";
export const CHAR_DIAMOND = "\u2666";
export const CHAR_HEART = "\u2665";
export const CHAR_SPADE = "\u2660";

function isInteger(s: string) {
    for (const c of s) {
        if (c < '0' || c > '9') {
            return false;
        }
    }
    return true;
}

function toInteger(s: string) {
    let number = 0;

    if (isInteger(s)) {
        number = Number(s);
    } else {
        console.warn("toInteger() not an integer");
    }
    return number;
}

export class Language {
    config?: Config;

    loadedId = LanguageId.NONE;
    get code() {
        switch (this.id) {
            case LanguageId.LANGUAGE_DUTCH:
            case LanguageId.LANGUAGE_GERMAN:
                return "nl-NL";
            case LanguageId.LANGUAGE_ENGLISH:
                return "en-AU";
            default:
                return "en-US";
        }
    }

    map: Map<string, string> = new Map;

    get id() {
        return this.loadedId ?? this.config?.languageId ?? LanguageId.NONE;
    }
    get cardId() {
        return this.config?.cardLanguageId ?? LanguageId.LANGUAGE_ENGLISH;
    }
    get fourColourDeck() {
        return this.config?.fourColourDeck ?? false;
    }

    async load(config: Config) {
        this.config = config;
        let filename = "";

        switch (config.languageId) {
            case LanguageId.LANGUAGE_ENGLISH:
                filename = "/language/English.strings";
                break;
            case LanguageId.LANGUAGE_DUTCH:
            default:
                filename = "/language/Nederlands.strings";
                break;
        }

        const file = new LanguageFile();
        await file.load(filename);
        this.map = file.map;
        this.loadedId = config.languageId;

        // Set HTML lang attribute to prevent browser auto-translation
        document.documentElement.lang = this.getHtmlLang();
    }

    getHtmlLang() {
        switch (this.loadedId) {
            case LanguageId.LANGUAGE_DUTCH:
                return "nl";
            case LanguageId.LANGUAGE_ENGLISH:
                return "en";
            case LanguageId.LANGUAGE_GERMAN:
                return "de";
            default:
                return "nl";
        }
    }

    choose(english?: string, dutch?: string) {
        switch (this.id) {
            case LanguageId.LANGUAGE_ENGLISH:
                return english ?? dutch ?? "";
            case LanguageId.LANGUAGE_DUTCH:
            default:
                return dutch ?? english ?? "";
        }
    }

    getLocale() {
        const locale = ["nl", "en-AU", "nl-NL", "de-DE"];
        return locale[this.id] ?? "nl";
    }

    getNumberFormat(options?: Intl.NumberFormatOptions) {
        const locale = this.getLocale();
        return new Intl.NumberFormat(locale, options);
    }

    isDutch() {
        return this.id === LanguageId.LANGUAGE_DUTCH;
    }

    isEnglish() {
        return this.id === LanguageId.LANGUAGE_ENGLISH;
    }

    isGerman() {
        return this.id === LanguageId.LANGUAGE_GERMAN;
    }

    getLanguageCode() {
        switch (this.id) {
            case LanguageId.LANGUAGE_DUTCH:
                return "NL";
            case LanguageId.LANGUAGE_ENGLISH:
                return "EN";
            case LanguageId.LANGUAGE_GERMAN:
                return "DU";
            default:
                return "N/A";
        }
    }

    htmlSuit(suitIndex?: number, suit?: string) {
        let s = "";

        if (!suitIndex) {
            if (suit) {
                suitIndex = "-CDHS".indexOf(suit);
            } else {
                suitIndex = 0;
            }
        }
        switch (suitIndex) {
            case SUIT.CLUBS:
                s = this.fourColourDeck ? HTML_CLUB_4C : HTML_CLUB;
                break;
            case SUIT.DIAMONDS:
                s = this.fourColourDeck ? HTML_DIAMOND_4C : HTML_DIAMOND;
                break;
            case SUIT.HEARTS:
                s = HTML_HEART;
                break;
            case SUIT.SPADES:
                s = HTML_SPADE;
                break;
            case SUIT.NOTRUMP:
                return this.H("STR_SUIT_NOTRUMPS");
            default:
        }

        return s
    }

    htmlFormat(text: string) {
        return this.colorize(text
            .replace(/\n/g, "<br>")
            .replace(/\\n/g, "<br>")
        );
    }
    /*
        htmlFormat(text: string) {
            let html = "";
    
            // search for special card symbols
            for (let l = 0; l < text.length; l++) {
                const c = text.charAt(l);
    
                switch (c) {
                    case '\n':
                        html += "<br>";
                        break;
                    case '\\':
                        if (text.charAt(l + 1) == 'n') {
                            html += "<br>";
                            l++;
                        } else {
                            html += c;
                        }
                        break;
                    default:
                        html += this.colorize(c);
                }
            }
            return html;
        }
        */

    colorize(text: string) {
        return text.
            replace(/♣/g, this.fourColourDeck ? HTML_CLUB_4C : HTML_CLUB).
            replace(/♦/g, this.fourColourDeck ? HTML_DIAMOND_4C : HTML_DIAMOND).
            replace(/♥/g, HTML_HEART).
            replace(/♠/g, HTML_SPADE);
    }

    // getActivitByType(type: number, name: string, shortstring?: boolean) {
    // 	return (this.getActivityType(type, shortstring) + " " + name).trim();
    // }

    getActivity(act: IActivityBase, shortstring?: boolean) {
        return (this.getActivityType(act.type, shortstring) + " " + act.name).trim();
    }

    // getActivityType(act: Activity, shortstring: boolean) {
    // 	return this.getActivityType(act.type, shortstring);
    // }

    getActivityType(type: number, shortstring?: boolean) {
        switch (type) {
            case ActivityType.NONE:
                return this.T("STR_ACTIVITY_TYPE_NONE");
            case ActivityType.COMPETITION:
                return this.T("STR_ACTIVITY_TYPE_COMPETITION");
            case ActivityType.TEAMS:
                return this.T("STR_ACTIVITY_TYPE_TEAMS");
            case ActivityType.TOURNAMENT:
                return shortstring ? this.T("STR_ACTIVITY_TYPE_TOURNAMENT_SHORT") : this.T("STR_ACTIVITY_TYPE_TOURNAMENT");
            case ActivityType.VUGRAPH:
                return shortstring ? this.T("STR_ACTIVITY_TYPE_VUGRAPH_SHORT") : this.T("STR_ACTIVITY_TYPE_VUGRAPH");
            case ActivityType.COURSE:
                return this.T("STR_ACTIVITY_TYPE_COURSE");
        }
        return "??";
    }

    getBoard(b?: Board) {
        return b ? this.getScoringMethod(b.scoringMethod) + " " + b.number : "";
    }

    getBoardShort(b?: Board) {
        return b ? this.getScoringMethod(b.scoringMethod).substring(0, 1) + b.number : "";
    }

    getClaimedResult(history: History, tricks: number) {
        const tricksClaimed = tricks - history.tricksDeclarer;
        const tricksRemaining = history.tricksRemaining;
        const tricksConceded = tricksRemaining - tricksClaimed;

        const result = this.getContractResultWithTricks(history, tricks);

        if (tricksClaimed == 0) {
            return this.H("MSG_CLAIM_ZERO_TRICKS", result);
        } else if (tricksClaimed === tricksRemaining) {
            return this.H("MSG_CLAIM_ALL_TRICKS", result);
        } else {
            if (tricksConceded === 1) {
                return this.H("MSG_CONCEDE_ONE_TRICK", result);
            }
            return this.H("MSG_CONCEDE_MULTIPLE_TRICKS",
                tricksConceded.toString(),
                result);
        }
    }

    getClaimedMessage(history: History, tricks: number, explanation: string) {
        const result = this.getClaimedResult(history, tricks);
        const txt = result.replace(/<br>/g, '');

        if (explanation.length > 0) {
            return `${txt} ${this.H("STR_EXPLANATION")}: ${explanation}`;
        }
        return txt;
    }

    getChatTo(target: number) {
        switch (target) {
            case ChatTo.NONE:
                return '';
            case ChatTo.PRIVATE:
            case ChatTo.PRIVATE_CONTROL:
                return '';
            case ChatTo.TABLE:
                return this.T("FMAIN_CHAT_FULL");
            case ChatTo.SPECTATORS:
                return this.T("FMAIN_CHAT_SPECTATORS");
            case ChatTo.LEFT:
                return this.T("FMAIN_CHAT_LEFT");
            case ChatTo.RIGHT:
                return this.T("FMAIN_CHAT_RIGHT");
            case ChatTo.EXPLANATION:
                return this.T("FMAIN_CHAT_EXPLANATION");
            case ChatTo.LOBBY:
                return this.T("FMAIN_CHAT_LOBBY");
            case ChatTo.TOURNAMENT_DIRECTOR:
                return this.T("FMAIN_CHAT_TOURNAMENT_DIRECTOR");
            case ChatTo.BROADCAST:
                return this.T("FMAIN_CHAT_ALL");
            case ChatTo.BROADCAST_SPECTATORS:
                return this.T("FMAIN_CHAT_BROADCAST_SPECTATORS");
            case ChatTo.SYSTEM:
                return this.T("");
        }
        return "";
    }

    /**
    * @return proper formatted decimal
    */
    getDecimal(d: number, fDigits: number, options?: Intl.NumberFormatOptions) {
        const o: Intl.NumberFormatOptions = {
            minimumFractionDigits: fDigits,
            maximumFractionDigits: fDigits, ...options
        };
        // Truncate ourselves instead of asking for roundingMode: 'trunc'. That option
        // is ignored on older engines, which would then round. The digits come from the
        // merged options, so a caller raising maximumFractionDigits keeps them.
        const digits = o.maximumFractionDigits ?? fDigits;
        const d2 = (o.roundingMode ?? 'trunc') === 'trunc' ? truncateDecimals(d, digits) : d;

        return this.getNumberFormat(o).format(d2);
    }

    getDecimalSign() {
        if (this.isDutch() || this.isGerman()) {
            return ',';
        }
        return '.';
    }

    getScoringMethod(scoringMethod: SCORING_METHOD) {
        switch (scoringMethod) {
            case SCORING_METHOD.MATCHPOINTS:
                return this.T("STR_MATCHPOINTS");
            case SCORING_METHOD.BUTLER:
                return this.T("STR_BUTLER");
            case SCORING_METHOD.TEAMS:
                return this.T("STR_TEAMS");
            case SCORING_METHOD.COURSE:
                return this.T("STR_COURSE");
            default:
                return "";
        }
    }

    getScoringMethodShort(scoringMethod: SCORING_METHOD) {
        return this.getScoringMethod(scoringMethod).substring(0, 1).toUpperCase();
    }

    getPlayingMode(pm: PLAYING_MODE) {
        switch (pm) {
            case PLAYING_MODE.RECREATIONAL:
                return this.T("STR_RECREATIONAL");
            case PLAYING_MODE.COMPETITION:
                return this.T("STR_COMPETITION");
            default: // Teams:
                return this.T("STR_COMPETITION");
        }
    }

    getPlayingModeShort(pm: PLAYING_MODE) {
        return this.getPlayingMode(pm).substring(0, 1);
    }

    getTableName(table: ITableBase) {
        return this.getTablePrettyName(table.name);
    }

    getTablePrettyName(rawname: string) {
        return rawname
            .replace(TABLE_NAME.LOBBY, this.T("STR_LOBBY"))
            .replace(TABLE_NAME.CLOSED, " " + this.T("STR_CLOSED"))
            .replace(TABLE_NAME.OPEN, " " + this.T("STR_OPEN"))
            .replace(TABLE_NAME.WAITING, this.T("STR_WAITING"));

    }

    getVulnerability(vul: Vulnerability) {
        switch (vul.value) {
            case VULNERABILITY.NONE:
                return this.T("STR_VULNERABILITY_NONE");
            case VULNERABILITY.NS:
                return this.T("STR_VULNERABILITY_NS");
            case VULNERABILITY.EW:
                return this.T("STR_VULNERABILITY_EW");
            case VULNERABILITY.BOTH:
                return this.T("STR_VULNERABILITY_BOTH");
            default:
                return "";
        }
    }

    getVulnerabilityShort(vul: Vulnerability) {
        switch (vul.value) {
            case VULNERABILITY.NONE:
                return "-";
            case VULNERABILITY.NS:
                return this.T("STR_VULNERABILITY_NS");
            case VULNERABILITY.EW:
                return this.T("STR_VULNERABILITY_EW");
            case VULNERABILITY.BOTH:
                return this.T("STR_VULNERABILITY_BOTH");
            default:
                return "";
        }
    }

    getSuitSymbolPlainText(suit: SUIT) {
        switch (suit) {
            case SUIT.CLUBS:
                return this.T("STR_SUIT_CLUBS");
            case SUIT.DIAMONDS:
                return this.T("STR_SUIT_DIAMONDS");
            case SUIT.HEARTS:
                return this.T("STR_SUIT_HEARTS");
            case SUIT.SPADES:
                return this.T("STR_SUIT_SPADES");
            case SUIT.NOTRUMP:
                return this.T("STR_SUIT_NOTRUMPS");
            default:
                return "";
        }
    }

    getSuitSymbol(suitIndex?: number, suit?: string) {
        let s = "";

        if (!suitIndex) {
            if (suit) {
                suitIndex = "-CDHS".indexOf(suit);
            } else {
                suitIndex = 0;
            }
        }
        switch (suitIndex) {
            case SUIT.CLUBS:
                s = this.fourColourDeck ? HTML_CLUB_4C : HTML_CLUB;
                break;
            case SUIT.DIAMONDS:
                s = this.fourColourDeck ? HTML_DIAMOND_4C : HTML_DIAMOND;
                break;
            case SUIT.HEARTS:
                s = HTML_HEART;
                break;
            case SUIT.SPADES:
                s = HTML_SPADE;
                break;
            case SUIT.NOTRUMP:
                return "<span>" + this.T("STR_SUIT_NOTRUMPS") + "</span>";
            default:
        }

        return s
    }

    getCardPlainText(c: Card) {
        if (c.isValid()) {
            return this.getSuitSymbolPlainText(c.suit) + this.getCardHeight(c);
        }
        return "";
    }

    getCard(c?: Card) {
        if (c && c.isValid()) {
            return this.getSuitSymbol(c.suit) + this.getCardHeight(c);
        }
        return '-';
    }

    getCardHeight(c: Card) {
        if (c.isValid()) {
            const nl = " AHVBT98765432";
            const uk = " AKQJT98765432";

            const i = c.height;

            switch (this.cardId) {
                case LanguageId.LANGUAGE_DUTCH:
                    return nl.substring(i, i + 1);
                default:
                    return uk.substring(i, i + 1);
            }
        }
        return "";
    }

    getLevel(level: Level) {
        return `${level.number}${this.getStars(level)}`;
    }

    getStars(level: Level) {
        let s = "";
        for (let i = 0; i < level.stars; i++)
            s += "*";

        return s;
    }

    getLevelDesc(user: HasLevels, scoringMethod: SCORING_METHOD): string {
        if (scoringMethod === SCORING_METHOD.ANY) {
            return this.getLevelDesc(user, SCORING_METHOD.MATCHPOINTS)
                + " / " + this.getLevelDesc(user, SCORING_METHOD.BUTLER);
        }

        return this.getScoringMethod(scoringMethod) + " " + this.getLevel(user.getLevel(scoringMethod));
    }

    translateNT(s: string) {
        if (this.id === LanguageId.LANGUAGE_DUTCH) {
            return s.replace("NT", "SA");
        }
        // default
        return s;
    }

    getBidPlainText(b: Bid) {
        switch (b.getInt()) {
            case CONTRACT.PASSEDOUT:
            case BID.PASS:
                return this.T("STR_BID_PASS");
            case BID.DOUBLE:
                return this.T("STR_BID_DOUBLE");
            case BID.REDOUBLE:
                return this.T("STR_BID_REDOUBLE");
            default:
                return b.height + this.getSuitSymbolPlainText(b.suit);
        }
    }

    getBid(b?: Bid) {
        if (b && b.isValid()) {
            switch (b.getInt()) {
                case CONTRACT.PASSEDOUT:
                case BID.PASS:
                    return this.T("STR_BID_PASS");
                case BID.DOUBLE:
                    return this.T("STR_BID_DOUBLE");
                case BID.REDOUBLE:
                    return this.T("STR_BID_REDOUBLE");
                default:
                    return b.height + this.getSuitSymbol(b.suit);
            }
        }
        return "<span></span>";
    }

    getContractPlainText(history?: History) {
        if (history) {
            const msg = this.getBidPlainText(history.contract);

            switch (history.contract.penalty) {
                case PENALTY.DOUBLE:
                    return msg + "X";
                case PENALTY.REDOUBLE:
                    return msg + "XX";
                default:
            }

            return msg;
        }
        return "";
    }

    getContract(contract?: Contract, declarer?: Chair) {
        if (contract) {
            let msg = this.getBid(contract);

            switch (contract.penalty) {
                case PENALTY.DOUBLE:
                    msg += "X";
                    break;
                case PENALTY.REDOUBLE:
                    msg += "XX";
                    break;
                default:
            }

            if (declarer?.isPlayer()) {
                msg += ` ${this.T('STR_BY')} ${this.getChairShort(declarer)}`;
            }

            return msg;
        }
        return "";
    }

    getContractWithDeclarer(contract: Contract, declarer: Chair) {
        if (contract.isContract()) {
            let msg = this.getContract(contract);

            if (declarer.isPlayer()) {
                msg += ` ${this.T('STR_BY')} ${this.getChairShort(declarer)}`;
            }

            return msg;
        }
        return "";
    }

    getContractResultWithDeclarerNoScore(contract: Contract, tricks: number, declarer: Chair) {
        switch (contract.getInt()) {
            case CONTRACT.PASSEDOUT:
            case BID.PASS:
                return this.T("STR_PASS_OUT");
            default:
        }

        let msg = this.getBid(contract);

        switch (contract.penalty) {
            case PENALTY.DOUBLE:
                msg += "X";
                break;
            case PENALTY.REDOUBLE:
                msg += "XX";
                break;
        }

        msg += " " + this.getContractTricks(contract, tricks);
        msg += " " + this.getChair(declarer).substring(0, 1).toUpperCase();

        return msg;
    }


    getContractTricks(contract: Bid, tricks: number) {
        if (contract.isContract() === false) {
            return "";
        }

        let overTricks = 0;

        switch (contract.getInt()) {
            case CONTRACT.PASSEDOUT:
            case BID.PASS:
                break;
            default:
                overTricks = tricks - contract.height - 6;
        }

        if (overTricks == 0) {
            return this.T("STR_CONTRACT_SHORT");
        } else if (overTricks > 0) {
            return "+" + overTricks;
        } else {
            return "" + overTricks;
        }
    }

    getContractResultWithTricks(history: History, tricks: number) {
        if (history === undefined)
            return "";

        let msg = "";

        switch (history.contract.getInt()) {
            case CONTRACT.PASSEDOUT:
            case BID.PASS:
                msg = this.T("STR_PASS_OUT");
                break;
            default:
                msg = this.getContract(history.contract)
                    + " " + this.getContractTricks(history.contract, tricks);
        }

        const isVulnerable = history.board.isVulnerable(history.declarer);
        const score = ContractResult.calculateScore(history.contract, tricks, isVulnerable);

        msg += this.choose(' scoring ', ' = ');

        if (score <= 0) {
            return msg + score;
        } else {
            return msg + "+" + score;
        }
    }

    getContractResultOfHistory(history?: History, options?: ContractResultOptions) {
        if (history !== undefined) {
            let opt: ContractResultOptions = {
                lead: history.getLead(),
                declarer: history.declarer,
                scoreNS: history.score,
                scoreTreatment: history.scoreTreatment,
                ...options
            }
            return this.getContractResult(history.contract, history.tricksDeclarer, opt);
        }
        return '';
    }

    getContractResult(contract: Contract, tricks: number, options?: ContractResultOptions) {
        const opt: ContractResultOptions = {
            scoreTreatment: SCORE_TREATMENT.NORMAL,
            ...options
        }

        if (opt.scoreTreatment === SCORE_TREATMENT.CANCELLED) {
            return this.getScoreTreatment(opt.scoreTreatment);
        }

        // 1SA C door N [♥4] = -90
        let msg = "";

        // CONTRACT
        switch (contract.getInt()) {
            case CONTRACT.PASSEDOUT:
            case BID.PASS:
                msg = this.T("STR_PASS_OUT");
                break;
            default:
                if (contract.isValid()) {
                    msg = this.getContract(contract)
                    if (opt.scoreTreatment !== SCORE_TREATMENT.NOT_FINISHED && opt.scoreTreatment !== SCORE_TREATMENT.ARTIFICIAL) {
                        msg += " " + this.getContractTricks(contract, tricks);
                    }
                }
        }

        // BY
        if (opt.declarer && contract.isPlayable()) {
            msg += " " + this.T("STR_BY") + " " + this.getChair(opt.declarer).substring(0, 1);
        }
        // LEAD
        if (opt.lead && opt.lead.isValid()) {
            msg += " [" + this.getCard(opt.lead) + "]";
        }

        if (opt.scoreTreatment !== SCORE_TREATMENT.NOT_FINISHED) {
            // SCORE
            if (opt.scoreNS) {
                let score = opt.scoreNS;

                if (contract.getInt() !== CONTRACT.NONE) {
                    if (opt.perspective && opt.perspective.isEW()) {
                        score = -score;
                    }

                    msg += this.choose(' scoring ', ' = ');

                    if (score <= 0) {
                        msg += score;
                    } else {
                        msg += "+" + score;
                    }
                }
            }
        }
        if (opt.scoreTreatment !== undefined) {
            const st = this.getScoreTreatment(opt.scoreTreatment, true);
            if (st.length > 0) {
                msg += ` (${st})`;
            }
        }
        return msg;
    }

    getScoreTreatment(st: SCORE_TREATMENT, shortstring?: boolean) {
        switch (st) {
            case SCORE_TREATMENT.NOT_FINISHED:
                return shortstring ? this.T("STR_UNFINISHED_SCORE_SHORT") : this.T("STR_UNFINISHED_SCORE");
            case SCORE_TREATMENT.CANCELLED:
                return shortstring ? this.T("STR_CANCELLED_SCORE_SHORT") : this.T("STR_CANCELLED_SCORE");
            case SCORE_TREATMENT.ARTIFICIAL:
            case SCORE_TREATMENT.REPLACEMENT:
                return shortstring ? this.T("STR_ARTIFICIAL_SCORE_SHORT") : this.T("STR_ARTIFICIAL_SCORE");
        }
        return "";
    }

    getCompetitiveScore(sm: SCORING_METHOD, score: number, imp100?: boolean) {
        if (sm === SCORING_METHOD.MATCHPOINTS) {
            return this.getDecimal(score / 100, 2) + "%";
        } else { // Butler
            const o: Intl.NumberFormatOptions = { signDisplay: "always" };

            if (imp100) { // imps has to be shown in 2 decimals (and divide by 100)
                return this.getDecimal(score / 100, 2, o) + " IMP";
            } else {
                return this.getDecimal(score, 0, o) + " IMP";
            }
        }
    }

    /**
     * The other side's score, as " (OW 60,00%)", after a split score: a ruling that gave the two
     * sides figures that do not mirror. Otherwise nothing; one side alone would understate it.
     */
    getSplitScoreSuffix(sm: SCORING_METHOD, scoreNS: number, scoreEW: number, perspective: Chair) {
        const mirrorSum = sm === SCORING_METHOD.MATCHPOINTS ? 10000 : 0;

        if (scoreNS + scoreEW === mirrorSum) {
            return "";
        }
        const fromEW = perspective.isEW();
        return ` (${this.T(fromEW ? "STR_NS" : "STR_EW")} ${this.getCompetitiveScore(sm, fromEW ? scoreNS : scoreEW)})`;
    }

    getIMP(imp: number) {
        return this.getDecimal(imp, 0, { signDisplay: "always", maximumFractionDigits: 2 }) + " IMP";
    }

    getVP(vp100: number) {
        return this.getDecimal(vp100 / 100, 2, { signDisplay: "always" }) + " VP"
    }

    /**
     * Returns a simple short string of the section ("lijn") in which a participant of a tournament can be seated.
     * @param section zero-based index of section
     * @return String of section (A/B/C/...)
     */
    getSection(section: number) {
        if (section === 0) {
            return "-";
        }
        if (section > 0 && section <= 26) {
            let c = 'A'.charCodeAt(0) + section - 1;
            return String.fromCharCode(c);
        }
        return "";
    }

    getChair(c: Chair | number) {
        let index = typeof (c) === 'object' ? c.index : c;
        switch (index) {
            case CHAIR.NONE:
                return "";
            case CHAIR.NORTH:
                return this.T("STR_CHAIR_NORTH");
            case CHAIR.EAST:
                return this.T("STR_CHAIR_EAST");
            case CHAIR.SOUTH:
                return this.T("STR_CHAIR_SOUTH");
            case CHAIR.WEST:
                return this.T("STR_CHAIR_WEST");
            case CHAIR.VISITOR:
                return this.T("STR_CHAIR_VISITOR");
            case CHAIR.SPECTATOR:
                return this.T("STR_CHAIR_SPECTATOR");
        }
        return "";
    }

    getChairShort(c: Chair | number) {
        return this.getChair(c).substring(0, 1);
    }

    getPlayersPerParticipant(ppp: number) {
        switch (ppp) {
            case 1:
                return this.T("STR_INDIVIDUAL");
            case 2:
                return this.T("STR_PAIRS");
            case 4:
                return this.T("STR_TEAMS");
        }
        return "";
    }

    transelateShortday(s: string) {
        const days: string[] = [
            "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"
        ];

        for (const day of days) {
            if (s.indexOf(day) > -1) {
                const tday = this.T(day);
                return s.replace(day, tday); // replaced a day
            }
        }
        return s;
    }

    // will output a natural spoken string of the time
    // p.e.: 3 dagen, 4 uur en 35 seconden
    getNaturalTimeDuration(sec: number) {
        let str = "";

        if (sec < 0) {
            str = this.T("STR_MINUS") + " ";
            sec = -sec;
        }

        let hours = Math.floor(sec / 3600);
        sec = Math.floor(sec % 3600);
        let min = Math.floor(sec / 60);
        sec = Math.floor(sec % 60);
        let days = Math.floor(hours / 24);
        hours = Math.floor(hours % 24);


        let as: string[] = [];

        if (days > 0) {
            as.push(`${days} ` + (days > 1 ? this.T("STR_DAYS").toLowerCase() : this.T("STR_DAY").toLowerCase()));
        }

        if (hours > 0) {
            as.push(`${hours} ` + (hours > 1 ? this.T("STR_HOURS").toLowerCase() : this.T("STR_HOUR").toLowerCase()));
        }

        if (min > 0) {
            as.push(`${min} ` + (min > 1 ? this.T("STR_MINUTES").toLowerCase() : this.T("STR_MINUTE").toLowerCase()));
        }

        // display seconds only when we don't have days and hours
        if ((sec > 0) && (days + hours == 0) && (min < 5)) {
            as.push(`${sec} ` + (sec > 1 ? this.T("STR_SECONDS").toLowerCase() : this.T("STR_SECOND").toLowerCase()));
        }

        str += this.getNaturalList(as);

        return str;
    }

    getSecondsToTime(sec: number) {
        let str = "";

        if (sec < 0) {
            str = "-";
            sec = -sec;
        }

        let hours = Math.floor(sec / 3600);
        sec = Math.floor(sec % 3600);
        let min = Math.floor(sec / 60);
        sec = Math.floor(sec % 60);

        let days = Math.floor(hours / 24);
        hours = Math.floor(hours % 24);

        if (days != 0) {
            str += `${days} ${(days > 1) ? this.T("STR_DAYS").toLowerCase() : this.T("STR_DAY").toLowerCase()} `;
        }

        const fo: Intl.NumberFormatOptions = { minimumIntegerDigits: 2 };
        const f = new Intl.NumberFormat(this.code, fo);

        if (hours > 0) {
            str += `${hours}:`
        }

        str += `${f.format(min)}:${f.format(sec)}`

        return str;
    }

    getSecondsToMinutes(sec: number) {
        const min = Math.floor(sec / 60);
        return `${min} ${min === 1 ? this.T("STR_MINUTE").toLowerCase() : this.T("STR_MINUTES").toLowerCase()}`;
    }

    getNaturalList(list: string[]) {
        let r = "";
        for (let i = 0; i < list.length; i++) {
            if (i > 0) {
                if (i < list.length - 1) {
                    r += ", ";
                } else {
                    r += " " + this.T("STR_AND") + " ";
                }
            }
            r += list[i];
        }
        return r;
    }

    getBold(s: string) {
        return "<b>" + s + "</b>";
    }

    getTeamDescription(t: RegistrationBase) {
        return this.T("STR_TEAM_COMP", t.player[0], t.player[1], t.player[2], t.player[3], t.player[0]);
    }

    getTableDesc(t?: Table) {
        if (t) {
            const sm = this.getScoringMethod(t.scoringMethod).substring(0, 1);
            const pm = this.getPlayingModeShort(t.playingMode).substring(0, 1);
            return `[${sm} ${t.levelFrom}-${t.levelTo} ${pm}]`;
        }
        return "";
    }

    getTournamentStructure(t: Tournament) {
        switch (t.getStructure()) {
            case TournamentStructure.MatchpointsPairs:
                return this.T('STR_MATCHPOINTS');
            case TournamentStructure.MatchpointsPairsIndividual:
                return this.T('STR_MATCHPOINTS') + ' - ' + this.T('STR_INDIVIDUAL');
            case TournamentStructure.MatchpointsButler:
                return this.T('STR_BUTLER');
            case TournamentStructure.MatchpointsButlerIndividual:
                return this.T('STR_BUTLER') + ' - ' + this.T('STR_INDIVIDUAL');
            case TournamentStructure.Teams:
                return this.T('STR_TEAMS');
            case TournamentStructure.TeamsHalftimeSwitch:
                return this.T('STR_TEAMS') + ' - ' + this.T('STR_TWO_HALVES');
        };
    }

    getContactCode(code: CONTACT_CODE) {
        // legacy codes
        if ((code >= 0) && (code <= 9)) {
            return code.toLocaleString();
        }

        // new codes
        for (const i of CONTACT_CODES) {
            if (code === i) {
                return this.T("CONTACT_CODE_" + code);
            }
        }

        return "";
    }

    /**
     * The short form for the contact list: the digit for the legacy codes, and for the
     * others the letter in front of the dash of the full label ("X - Niet Indelen" -> "X").
     * Cuts the stored text and not getContactCode()'s output, because that has been
     * through colorize() and can carry a dash inside markup (class="suit-clubs").
     */
    getContactCodeShort(code: CONTACT_CODE) {
        if (this.getContactCode(code).length === 0) {
            return "";
        }
        if ((code >= 0) && (code <= 9)) {
            return code.toLocaleString();
        }
        const key = "CONTACT_CODE_" + code;
        const label = this.translate(key);

        // translate() answers with the key itself when the string isn't there (yet); a new
        // code shouldn't print CONTACT_CODE_20 into the narrow column.
        if (label === key) {
            return "";
        }
        const dash = label.indexOf('-');

        return dash > 0 ? label.substring(0, dash).trim() : label;
    }

    translate(key: string) {
        return this.map.get(key) ?? this.map.get(key.toUpperCase()) ?? key;
    }

    T(key: string, ...param: string[]) {
        let s = this.translate(key);

        if (param) {
            s = this.createMessage(s, param, true);
        }
        return s;
    }

    H(key: string, ...param: string[]) {
        let s = this.translate(key);

        if (param) {
            s = this.createMessage(s, param, true);
        }
        return this.htmlFormat(s);
    }

    MD(key: string, ...param: string[]) {
        let s = this.translate(key);

        if (param) {
            s = this.createMessage(s, param, true);
        }
        return this.htmlFormat(simpelMDtoHtml(s));
    }

    format(format: string, value: string, withTags: boolean) {
        let out = "";

        switch (format) {
            case '$':
                out = withTags ? Tagger.createUserTag(value) : value;
                break;
            case '@':
                out = withTags ? Tagger.createTableTag(value) : this.getTablePrettyName(value);
                break;
            case '#':
                out = this.getTablePrettyName(value);
                break;
            case 'v':
                out = value + " " + this.T("STR_VP");
                break;
            case 'V':
                out = this.getDecimal(toInteger(value) / 100, 2) + " " + this.T("STR_VP");
                break;
            case 'i':
                out = value + " " + this.T("STR_IMP");
                break;
            case 'I':
                out = this.getDecimal(toInteger(value) / 100, 2) + " " + this.T("STR_IMP");
                break;
            case 'p':
            case '%':
                out = this.getDecimal(toInteger(value) / 100, 2) + "%";
                break;
            case 'd':
                out = this.getNaturalTimeDuration(toInteger(value));
                break;
            case 'f':
                out = value.replace('.', this.getDecimalSign());
                break;
            case 'F':
                out = this.getDecimal(toInteger(value) / 100, 2);
                break;
            default:
                out = value;
        }
        return out;
    }

    /**
     * All messages and translations must go through this method to replace all the
     * positional markers (like \1, \2, etc) with a string given in params.
     * The positional markers can have a format specifier, like:
     * @example
     * createMessage("\$1 just went to table \@2.", ["Jan", "Klas"], true);
     * 
     *  \$ for user name (make it taggable)
     *  \@ for table name (make it taggable and format lobby name)
     *  \# for table name without makeing it taggable
     *  \v for VP score, just adding VP to it
     *  \V for VP score devided 100, 2 decimal precision
     *  \i for IMP score, just adding IMP to it
     *  \I for IMP score devided 100, 2 decimal precision
     *  \p or \% for percentige, devided by 100, 2 decimal precision
     *  \d for a natural description of duration, like 2 minutes and 45 seconds.
     *  \f for converting a float, translating the .
     *  \F for float, but devide by 100, 2 decimal precision
     * 
     * @param s the string to parse
     * @param params an array of string to have the slots \1 replaced with
     * @param withTags create clickable html tags from user and table names
     * @returns 
     */
    createMessage(s: string, params: string[], withTags?: boolean) {
        let out = "";

        let inEscape = false;
        let formatSpecifier = '\x00';
        let position = 0;

        let length = s.length;
        let i = 0;
        let j = s.indexOf('\\');

        while (j >= i) {
            // Hey, an new escape sequence!
            inEscape = true;
            position = 0;
            formatSpecifier = '\x00';

            // copy to out till \
            if (j > i) {
                out += s.substring(i, j);
            }
            i = j + 1;

            while (inEscape && i < length) {
                let nextChar = s.charAt(i);
                i++;

                if (nextChar == '\\') {
                    if (position > 0) {
                        // Finish the old one first like \1\2
                        if (position <= params.length) {
                            out += this.format(formatSpecifier, params[position - 1], withTags ?? true);
                        }
                        position = 0;
                        formatSpecifier = '\x00';
                    } else {
                        // Just plain old slash
                        out += nextChar;
                        inEscape = false;
                    }
                } else {
                    if (nextChar >= '0' && nextChar <= '9') {
                        // Numbers just change the location
                        position = position * 10 + (nextChar.charCodeAt(0) - '0'.charCodeAt(0));
                    } else if (position == 0 && formatSpecifier === '\x00') {
                        // The first thing after the slash is the conversion specifier
                        formatSpecifier = nextChar;
                    } else {
                        if (position > 0 && position <= params.length) {
                            out += this.format(formatSpecifier, params[position - 1], withTags ?? true);
                        } else if (formatSpecifier !== '\x00') {
                            // we ate a char for formatspecifier, but we have no position
                            out += formatSpecifier;
                        }
                        out += nextChar;
                        inEscape = false;
                    }
                }
            }
            // not in escape or eof
            j = s.indexOf('\\', i);
        }

        if (inEscape) {
            // last chars where a \something
            if (position > 0 && position <= params.length) {
                out += this.format(formatSpecifier, params[position - 1], withTags ?? true);
            }
        } else if (i < length) {
            // write rest to out
            out += s.substring(i, length);
        }

        return out;
    }


    static stripMultipleSpaces(s: string) {
        let r = "";
        let prev = 'x';

        for (const c of s) {
            if (prev !== ' ' || c !== ' ') {
                prev = c;
                r += c;
            }
        }
        return r;
    }
}